/* "Później" and "Gotowe", standing ALONE under the card at the card's width.
 *
 * Owner, 18 September 2026: do not move them into the card's footer. They speak about the TRAINER's
 * own list; the buttons inside the card speak about the content ("Skomentuj", "Oznacz jako
 * przejrzane"). Keeping them apart is what keeps the two statements from reading as one row of
 * four equal acts.
 *
 * Direction B2C (owner, 5 November 2026) keeps them outside the card and joins them to its foot as
 * one plate: one filled, edged, raised box holding both, a hairline between them, its top corners
 * square against the card's square bottom ones. */

.bar {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-0);
  width: 100%;
  padding: var(--space-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: 0 0 var(--r-card) var(--r-card);
  box-shadow: var(--shadow-card);
}

.row {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-0) var(--space-4);
  background: transparent;
  border: 0;
  border-radius: var(--radius-xl);
}

.row + .row {
  border-left: var(--border-hairline) solid var(--border);
  border-radius: 0;
}

.row:hover {
  background: var(--bg-inset);
}

.act {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: var(--space-3);
  align-items: center;
  height: 52px;
  font-size: var(--fs-button);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-name);
  text-align: left;
  color: var(--text);
  background: none;
  border: 0;
}

/* The focus ring on this screen is INK and follows the control's own radius.
 *
 * The owner saw an EMBER RECTANGLE standing around the pill of "Gotowe". Two causes, both here:
 * the ring was `--accent`, and it was drawn on `.act`, which has no radius of its own, so the
 * outline squared off the corners of the `--radius-xl` pill it sits inside. Drawing it on the row
 * that owns the pill fixes the shape; `--text` fixes the colour, and matches the ring the owner
 * settled on for the exercise library on 18 September. Ember stays a scalpel — here it marks one
 * thing only, the muted-kind dot on the cog.
 *
 * `:focus-visible`, never `:focus`: the ring belongs to the keyboard. It does appear after a mouse
 * click followed by a KEY (the Z-to-undo flow) — that is Chromium re-arming the heuristic on the
 * keypress, and it is correct behaviour, not the defect that was reported. */
.row:has(.act:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Refused rather than removed: the card already has an answer, and a button that vanished would
 * move the one beside it. Never `opacity` — dimming multiplies the ink's contrast down and takes
 * the focus ring with it. The chip fill is the whole of the refusal's look since B2C took the rows'
 * own edges away. */
.rowAnswered {
  background: var(--bg-chip);
}

.act[aria-disabled='true'] {
  color: var(--text-2);
  cursor: not-allowed;
}

.glyph {
  display: grid;
  flex: none;
  place-items: center;
  width: 34px;
  height: 34px;
  color: var(--text-2);
  background: var(--bg-sunken);
  border-radius: var(--r-circle);
}

/* The one place `--good-ink` appears on this screen: the tick that says the act is a completion.
 * A SHAPE in that green and never a glow. */
.glyphDone {
  color: var(--good-ink);
}

.glyphMark {
  width: 17px;
  height: 17px;
}

.words {
  min-width: 0;
  font-weight: var(--fw-semibold);
}

.hint {
  display: block;
  font-size: var(--fs-label);
  font-weight: var(--fw-regular);
  letter-spacing: var(--ls-block);
  color: var(--text-3);
}

/* The caret that opens the four terms sits BESIDE the act rather than inside it — a control inside
 * a control is invalid HTML — so the row is a plain box holding the button and the caret. */
.tail {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin-left: auto;
}

.terms {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  color: var(--text-2);
  background: none;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-9-5px);
}

.terms:hover {
  color: var(--text);
  background: var(--bg-chip);
}

.terms:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.termsGlyph {
  width: 15px;
  height: 15px;
}

.key {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-5px);
}

/* The instant a term resolves to, stated beside its word in the menu. Tabular, because four of them
 * stack and a ragged column of times is harder to scan than the words above it. */
.termWhen {
  flex: none;
  font-size: var(--fs-label);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* The sentence under the bulk menu's two acts, saying why feedback has no bulk "gotowe". */
.bulkNote {
  padding: var(--space-2) var(--space-2) var(--space-1);
  margin-top: var(--space-1);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  border-top: var(--border-hairline) solid var(--border);
}

/* ── The two menus on this screen: the terms above and the bulk menu in the index ────────────
 * B2C (owner, 5 November 2026): 320 wide, and the group's label in sentence case at a readable size
 * rather than the shared surface's small capitals. Both through this screen's own classes, so every
 * other menu in the app keeps `shared/ui/MenuSurface`'s look: the width through the surface's own
 * resize property, the label scoped under the popup so it outranks `.groupLabel` by specificity
 * rather than by stylesheet order. */
.menu {
  --menu-min-width: 320px;
}

.menu .menuLabel {
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  letter-spacing: normal;
  text-transform: none;
  color: var(--text-3);
}
